"use client";

import { useRef } from "react";
import styles from "./TimeInput.module.css";

const sanitize = (v) => v.replace(/\D/g, "").slice(0, 2);

const clamp = (num, max) => {
  if (Number.isNaN(num)) return 0;
  return Math.min(Math.max(num, 0), max);
};

const TimeInput = ({
  value = { hh: "", mm: "", ss: "" },
  onChange = () => {},
}) => {
  const hRef = useRef(null);
  const mRef = useRef(null);
  const sRef = useRef(null);

  const update = (key, val) => {
    onChange({
      hh: value.hh ?? "",
      mm: value.mm ?? "",
      ss: value.ss ?? "",
      [key]: val,
    });
  };

  const handleChange = (key, max, e, nextRef) => {
    let raw = sanitize(e.target.value);

    if (raw === "") {
      update(key, "");
      return;
    }

    if (raw.length === 2) {
      raw = String(clamp(Number(raw), max)).padStart(2, "0");
      if (nextRef?.current) nextRef.current.focus();
    }

    update(key, raw);
  };

  const handleBlur = (key, max) => {
    const v = value[key];

    if (v === "") return;

    const num = clamp(Number(v), max);
    update(key, String(num).padStart(2, "0"));
  };

  const handleKeyDown = (e, key, max, prevRef, nextRef) => {
    const current = Number(value[key] || 0);

    switch (e.key) {
      case "ArrowUp":
        e.preventDefault();
        update(key, String(clamp(current + 1, max)).padStart(2, "0"));
        break;

      case "ArrowDown":
        e.preventDefault();
        update(key, String(clamp(current - 1, max)).padStart(2, "0"));
        break;

      case "ArrowLeft":
        if (prevRef?.current) {
          e.preventDefault();
          prevRef.current.focus();
        }
        break;

      case "ArrowRight":
        if (nextRef?.current) {
          e.preventDefault();
          nextRef.current.focus();
        }
        break;

      case "Backspace":
        if (value[key] === "") {
          if (prevRef?.current) {
            e.preventDefault();
            prevRef.current.focus();
          }
        }
        break;

      default:
        break;
    }
  };

  return (
    <div className={styles.wrapper}>
      <input
        ref={hRef}
        value={value.hh}
        placeholder="00"
        inputMode="numeric"
        maxLength={2}
        onChange={(e) => handleChange("hh", 23, e, mRef)}
        onBlur={() => handleBlur("hh", 23)}
        onKeyDown={(e) =>
          handleKeyDown(e, "hh", 23, null, mRef)
        }
      />

      <span>:</span>

      <input
        ref={mRef}
        value={value.mm}
        placeholder="00"
        inputMode="numeric"
        maxLength={2}
        onChange={(e) => handleChange("mm", 59, e, sRef)}
        onBlur={() => handleBlur("mm", 59)}
        onKeyDown={(e) =>
          handleKeyDown(e, "mm", 59, hRef, sRef)
        }
      />

      <span>:</span>

      <input
        ref={sRef}
        value={value.ss}
        placeholder="00"
        inputMode="numeric"
        maxLength={2}
        onChange={(e) => handleChange("ss", 59, e)}
        onBlur={() => handleBlur("ss", 59)}
        onKeyDown={(e) =>
          handleKeyDown(e, "ss", 59, mRef, null)
        }
      />
    </div>
  );
};

export default TimeInput;

